import { useCallback, useEffect } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { ArrowBigUp, MessageCirclePlus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useRoutes } from "@/contexts/use-routes"
import { cn } from "@/lib/utils"

type StartNewChatButtonProps = {
  onStartNewChatClick: () => void
  navigateOnSelect?: boolean
  className?: string
  iconOnly?: boolean
}

export function StartNewChatButton({
  onStartNewChatClick,
  navigateOnSelect = false,
  className,
  iconOnly = false,
}: StartNewChatButtonProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { routes } = useRoutes()
  const defaultRoute = routes.find((r) => r.default_route)
  const homePath = defaultRoute?.path ?? "/explorer"

  const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().includes("MAC")

  const handleClick = useCallback(() => {
    onStartNewChatClick()
    if (navigateOnSelect) {
      void navigate(homePath)
    }
  }, [onStartNewChatClick, navigateOnSelect, navigate, homePath])

  // Global keyboard shortcut: Cmd+Shift+O (Mac) or Ctrl+Shift+O (Windows/Linux)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "o") {
        e.preventDefault()
        handleClick()
      }
    }

    window.addEventListener("keydown", handleGlobalKeyDown)
    return () => window.removeEventListener("keydown", handleGlobalKeyDown)
  }, [handleClick])
  const shortcutHint = (
    <kbd className="bg-muted text-muted-foreground dark:border-border dark:bg-muted/50 inline-flex h-5 items-center justify-center gap-1 rounded border px-1.5 text-[10px] font-medium select-none">
      <span className="text-xs leading-tight">{isMac ? "⌘" : "Ctrl"}</span>
      <ArrowBigUp className="size-3" />
      <span className="font-sans text-[11px] leading-tight">O</span>
    </kbd>
  )

  if (iconOnly) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid="start-new-chat-button-icon"
            size="icon"
            variant="ghost"
            className={cn(
              "size-10 rounded-md transition-[color,background-color,box-shadow,scale] duration-200",
              "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1",
              "active:scale-[0.99]",
              className
            )}
            onClick={handleClick}
          >
            <MessageCirclePlus className="size-6" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p className="flex items-center gap-2">
            {t("questioninput.start_new_chat")}
            {shortcutHint}
          </p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Button
      data-testid="start-new-chat-button-large"
      variant="ghost"
      className={cn(
        "group/new text-foreground h-9 w-full justify-start rounded-lg px-2 text-sm transition-[color,background-color,box-shadow,scale] duration-150",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1",
        "active:scale-[0.98]",
        className
      )}
      onClick={handleClick}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MessageCirclePlus className="size-4 shrink-0" />
          <span className="translate-y-px truncate font-semibold">
            {t("questioninput.start_new_chat")}
          </span>
        </div>
        <span className="opacity-0 transition-opacity group-hover/new:opacity-100 group-focus-visible/new:opacity-100">
          {shortcutHint}
        </span>
      </div>
    </Button>
  )
}
